/**
 * Shared MenuConfigEditor — used by web template-new and event-new pages.
 * Mirrors the mobile /app/mobile/src/MenuConfigEditor.tsx contract.
 *   - "none": no menu step
 *   - "simple": flat list of meal options
 *   - "detailed": three optional courses (Starter 0-5, Main 2-5 required, Dessert 0-5)
 */
import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { CircleDot, Circle } from "lucide-react";

export function createEmptyMenuConfig() {
  return {
    mode: "none",
    meal_options: [],
    starter_enabled: false,
    starter_options: [],
    main_enabled: true,
    main_options: [],
    dessert_enabled: false,
    dessert_options: [],
  };
}

export function menuConfigFromRaw(raw) {
  if (!raw) return createEmptyMenuConfig();
  return {
    mode: raw.mode || "none",
    meal_options: raw.meal_options || [],
    starter_enabled: !!raw.starter_enabled,
    starter_options: raw.starter_options || [],
    main_enabled: raw.main_enabled !== false,
    main_options: raw.main_options || [],
    dessert_enabled: !!raw.dessert_enabled,
    dessert_options: raw.dessert_options || [],
  };
}

export function validateMenuConfig(v) {
  if (v.mode === "detailed") {
    if (v.main_options.length < 2) return "Add at least 2 main course options.";
    if (v.main_options.length > 5) return "Main course allows at most 5 options.";
    if (v.starter_options.length > 5) return "Starter allows at most 5 options.";
    if (v.dessert_options.length > 5) return "Dessert allows at most 5 options.";
  }
  if (v.mode === "simple" && v.meal_options.length === 0) {
    return "Add at least one meal option (or switch off menu choice).";
  }
  return null;
}

function RadioRow({ active, title, hint, onClick, testId }) {
  const Icon = active ? CircleDot : Circle;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={[
        "w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-md border transition-colors",
        active ? "bg-primary/5 border-primary" : "bg-white border-border hover:bg-muted/40",
      ].join(" ")}
    >
      <Icon size={18} className="text-primary mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
    </button>
  );
}

function joinList(arr) { return (arr || []).join(", "); }
function parseList(str, max) {
  return String(str || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, max);
}

function ListInput({ label, testId, value, max, onChange, placeholder }) {
  const [raw, setRaw] = useState(joinList(value));
  // Only pull from `value` when its canonical shape actually differs from
  // what our raw string would emit — otherwise every keystroke would round-
  // trip through parseList() and lose trailing spaces/commas mid-type.
  useEffect(() => {
    const incoming = joinList(value);
    const mine = joinList(parseList(raw, max));
    if (incoming !== mine) setRaw(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        data-testid={testId}
        value={raw}
        placeholder={placeholder}
        onChange={(e) => { setRaw(e.target.value); onChange(parseList(e.target.value, max)); }}
      />
    </div>
  );
}

export default function MenuConfigEditor({ value, onChange }) {
  const v = value || createEmptyMenuConfig();
  const set = (patch) => onChange({ ...v, ...patch });
  const mainTooFew = v.mode === "detailed" && v.main_options.length < 2;
  const starterTooMany = v.starter_options.length > 5;
  const mainTooMany = v.main_options.length > 5;
  const dessertTooMany = v.dessert_options.length > 5;

  return (
    <div className="space-y-2" data-testid="menu-config-editor">
      <Label>Menu selection</Label>
      <div className="space-y-2">
        <RadioRow
          active={v.mode === "none"}
          title="No menu choice"
          hint="Bookings skip the menu step."
          testId="menu-mode-none"
          onClick={() => set({ mode: "none" })}
        />
        <RadioRow
          active={v.mode === "simple"}
          title="Simple — pick one meal option"
          hint="A single list. Add Vegetarian/Vegan as one of the options."
          testId="menu-mode-simple"
          onClick={() => set({ mode: "simple" })}
        />
        <RadioRow
          active={v.mode === "detailed"}
          title="Detailed — three-course menu"
          hint="Starter / Main / Dessert. Main always required (2-5 options)."
          testId="menu-mode-detailed"
          onClick={() => set({ mode: "detailed" })}
        />
      </div>

      {v.mode === "simple" && (
        <div className="mt-3 p-4 rounded-md border border-border bg-muted/30 space-y-2">
          <ListInput
            label="Meal options (comma separated, up to 8)"
            testId="menu-simple-options"
            value={v.meal_options}
            max={8}
            placeholder="Beef Wellington, Pan-fried Salmon, Vegetarian/Vegan"
            onChange={(next) => set({ meal_options: next })}
          />
          <div className="text-xs text-muted-foreground">
            Each attendee will pick one option at checkout.
          </div>
        </div>
      )}

      {v.mode === "detailed" && (
        <div className="mt-3 p-4 rounded-md border border-border bg-muted/30 space-y-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox
              checked={v.starter_enabled}
              onCheckedChange={(x) => set({ starter_enabled: !!x })}
              data-testid="menu-starter-enable"
            />
            Include Starter course (optional, 0-5 options)
          </label>
          {v.starter_enabled && (
            <div className="pl-6 space-y-1">
              <ListInput
                label="Starter options (comma separated, max 5)"
                testId="menu-starter-options"
                value={v.starter_options}
                max={5}
                placeholder="Soup of the Day, Smoked Salmon, ..."
                onChange={(next) => set({ starter_options: next })}
              />
              {starterTooMany && <div className="text-xs text-destructive">Max 5 starters.</div>}
            </div>
          )}

          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox checked disabled />
            Main course (required, 2-5 options)
          </label>
          <div className="pl-6 space-y-1">
            <ListInput
              label="Main options (comma separated, 2-5)"
              testId="menu-main-options"
              value={v.main_options}
              max={5}
              placeholder="Beef Wellington, Pan-fried Salmon, Vegetarian/Vegan"
              onChange={(next) => set({ main_options: next })}
            />
            {mainTooFew && <div className="text-xs text-destructive">At least 2 main options required.</div>}
            {mainTooMany && <div className="text-xs text-destructive">Max 5 mains.</div>}
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox
              checked={v.dessert_enabled}
              onCheckedChange={(x) => set({ dessert_enabled: !!x })}
              data-testid="menu-dessert-enable"
            />
            Include Dessert course (optional, 0-5 options)
          </label>
          {v.dessert_enabled && (
            <div className="pl-6 space-y-1">
              <ListInput
                label="Dessert options (comma separated, max 5)"
                testId="menu-dessert-options"
                value={v.dessert_options}
                max={5}
                placeholder="Sticky Toffee Pudding, Cheese Board, ..."
                onChange={(next) => set({ dessert_options: next })}
              />
              {dessertTooMany && <div className="text-xs text-destructive">Max 5 desserts.</div>}
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            Each attendee chooses one option from every enabled course at checkout.
          </div>
        </div>
      )}
    </div>
  );
}
